import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  Archive,
  ArchiveRestore,
  ChevronLeft,
  Clock,
  DollarSign,
  Film,
  Loader2,
  RefreshCw,
  Sparkles,
  Trash2,
  Wand2,
} from 'lucide-react';

import { cn } from '@/lib/cn';
import { useHaptics } from '@/hooks/useHaptics';
import { usePuoCreareAvatar } from '@/hooks/usePuoCreareAvatar';
import { Loader } from '@/components/common/Loader';
import { ChromaKeyVideo } from '@/components/avatars/ChromaKeyVideo';
import { DualStreamVideo } from '@/components/avatars/DualStreamVideo';
import {
  useAnimate2dMutation,
  useDeleteVideoClipMutation,
  useGetAvatarQuery,
  useSetActiveVideoClipMutation,
  useUpdateVideoClipMutation,
  type AvatarVideoClip,
} from '@/services/avatarApi';
import { supportsVp9Alpha } from '@/lib/videoAlphaSupport';
import { useLinguaFormato } from '@/hooks/useLinguaFormato';
import { formatData, formatOra } from '@/lib/fmt';
import { PromptInIngleseHint } from '@/components/avatars/PromptInIngleseHint';

const CHROMA_KEY_PROMPT_PREFIX =
  `On a perfectly uniform, pure solid magenta background (hex #FF00FF, 100% saturation, no gradients, no shadows on the background). ` +
  `Critical: the subject and its clothing/props must NOT contain any magenta, pink, hot pink, or pinkish-purple anywhere — replace any such colors with neutral tones. ` +
  `Action: `;
function stripPrefix(p: string): { sys: string | null; user: string } {
  if (p.startsWith(CHROMA_KEY_PROMPT_PREFIX)) {
    return { sys: CHROMA_KEY_PROMPT_PREFIX.trim(), user: p.slice(CHROMA_KEY_PROMPT_PREFIX.length).trim() };
  }
  return { sys: null, user: p };
}

const PROVIDER_LABEL: Record<string, string> = {
  'hailuo-fal':     'Hailuo (MiniMax)',
  'kling-fal':      'Kling 2.1',
  'pixverse-fal':   'PixVerse V6',
  'vidu-fal':       'Vidu Q3',
  'veo31-fal':      'Veo 3.1 Standard',
  'veo31-fast-fal': 'Veo 3.1 Fast',
};

function fmtTime(iso: string | undefined, lingua: string): string {
  if (!iso) return '—';
  return `${formatData(iso, undefined, lingua)} ${formatOra(iso, lingua)}`;
}

function fmtWall(startedAt?: string, completedAt?: string): string {
  if (!startedAt || !completedAt) return '—';
  const ms = new Date(completedAt).getTime() - new Date(startedAt).getTime();
  if (ms < 0) return '—';
  return `${Math.round(ms / 1000)}s`;
}

function isVeo(p?: string): boolean {
  return p === 'veo31-fal' || p === 'veo31-fast-fal';
}

/**
 * Dedicated route /avatars/:id/clips/:clipId — full detail page for a single
 * video clip. Replaces the modal as the canonical "inspect a clip" surface.
 * Shows: video player (matting / chroma-key / opaque), user + system prompts,
 * full options including seed, run metrics, output URLs, and per-clip
 * actions (Set active, Archive, Delete, Retry, Reroll).
 */
export function AvatarClipDetail() {
  const { id, clipId } = useParams<{ id: string; clipId: string }>();
  const navigate = useNavigate();
  const lingua = useLinguaFormato();
  const haptics = useHaptics();
  // I-416: ritentare, rigenerare e rilanciare una clip SPENDE (animate-2d): con il flag Studio acceso (solo staging)
  // solo `administrator`; flag spento, come oggi (I-270).
  const puoCreare = usePuoCreareAvatar();
  const { t } = useTranslation('avatars');
  const [detailsOpen, setDetailsOpen] = useState(false);
  const { data: avatar, isLoading } = useGetAvatarQuery(id ?? '', { skip: !id });

  const [animate2d] = useAnimate2dMutation();
  const [updateClip] = useUpdateVideoClipMutation();
  const [deleteClip] = useDeleteVideoClipMutation();
  const [setActive] = useSetActiveVideoClipMutation();

  const clip: AvatarVideoClip | null = useMemo(() => {
    if (!avatar || !clipId) return null;
    return (avatar.videoClips ?? []).find(c => c.clipId === clipId) ?? null;
  }, [avatar, clipId]);

  const onRetry = useCallback(async () => {
    if (!id || !clip) return;
    const { user } = stripPrefix(clip.prompt);
    try {
      await animate2d({
        avatarId: id,
        prompt: user,
        provider: clip.provider,
        durationSec: (clip.durationSec ?? 6) as 5 | 6 | 10,
        resolution: clip.resolution,
        label: `${clip.label ?? user} (retry)`,
        transparency: clip.transparency,
        seed: clip.seed,
      }).unwrap();
      haptics.notification('success');
      navigate(`/avatars/${id}`);
    } catch { haptics.notification('error'); }
  }, [id, clip, animate2d, haptics, navigate]);

  // Editable motion prompt — initialized from the persisted clip prompt
  // (stripped of the chroma-key wrapper) so the user can tweak the action
  // and submit a new generation without leaving this page.
  const initialPromptText = useMemo(() => clip ? stripPrefix(clip.prompt).user : '', [clip]);
  const [editPrompt, setEditPrompt] = useState(initialPromptText);
  useEffect(() => { setEditPrompt(initialPromptText); }, [initialPromptText]);
  const editTrimmed = editPrompt.trim();
  const editDirty = editTrimmed.length >= 3 && editTrimmed !== initialPromptText.trim();

  const onRegenerate = useCallback(async () => {
    if (!id || !clip || !editDirty) return;
    try {
      await animate2d({
        avatarId: id,
        prompt: editTrimmed,
        provider: clip.provider,
        durationSec: (clip.durationSec ?? 6) as 5 | 6 | 10,
        resolution: clip.resolution,
        label: `${clip.label ?? editTrimmed} (edit)`,
        transparency: clip.transparency,
      }).unwrap();
      haptics.notification('success');
      navigate(`/avatars/${id}`);
    } catch { haptics.notification('error'); }
  }, [id, clip, editDirty, editTrimmed, animate2d, haptics, navigate]);

  const onReroll = useCallback(async () => {
    if (!id || !clip) return;
    const { user } = stripPrefix(clip.prompt);
    try {
      await animate2d({
        avatarId: id,
        prompt: user,
        provider: clip.provider,
        durationSec: (clip.durationSec ?? 6) as 5 | 6 | 10,
        resolution: clip.resolution,
        label: `${clip.label ?? user} (reroll)`,
        transparency: clip.transparency,
      }).unwrap();
      haptics.notification('success');
      navigate(`/avatars/${id}`);
    } catch { haptics.notification('error'); }
  }, [id, clip, animate2d, haptics, navigate]);

  const onSetActive = useCallback(async () => {
    if (!id || !clip) return;
    try {
      await setActive({ avatarId: id, clipId: clip.clipId }).unwrap();
      haptics.notification('success');
    } catch { haptics.notification('error'); }
  }, [id, clip, setActive, haptics]);

  const onToggleArchive = useCallback(async () => {
    if (!id || !clip) return;
    try {
      await updateClip({ avatarId: id, clipId: clip.clipId, archived: !clip.archived }).unwrap();
      haptics.impact('light');
    } catch { haptics.notification('error'); }
  }, [id, clip, updateClip, haptics]);

  const onDelete = useCallback(async () => {
    if (!id || !clip) return;
    if (!confirm(t('clips.deleteOne'))) return;
    try {
      await deleteClip({ avatarId: id, clipId: clip.clipId }).unwrap();
      haptics.notification('warning');
      navigate(`/avatars/${id}`);
    } catch { haptics.notification('error'); }
  }, [id, clip, deleteClip, haptics, navigate, t]);

  if (isLoading) return <Loader />;
  if (!avatar || !clip) {
    return (
      <div className="flex flex-col items-center gap-3 px-4 py-12 text-center text-sm text-fg-muted">
        <p>{t('clipPage.notFound')}</p>
        <Link to={`/avatars/${id ?? ''}`} className="rounded-full bg-bg-secondary px-3 py-1.5 text-xs font-semibold text-fg">
          {t('clipPage.backToAvatar')}
        </Link>
      </div>
    );
  }

  const { sys } = stripPrefix(clip.prompt);
  const providerLabel = PROVIDER_LABEL[clip.provider] ?? clip.provider;
  const transparencyLabels: Record<string, string> = {
    'none': t('clipDetails.transparency.none'),
    'chroma-key': t('clipDetails.transparency.chromaKey'),
    'matting': t('clipDetails.transparency.matting'),
  };
  const transparencyLabel = transparencyLabels[clip.transparency ?? 'none'] ?? clip.transparency ?? 'none';
  const costUsd = typeof clip.costCents === 'number' ? (clip.costCents / 100).toFixed(2) : null;
  const isActive = avatar.activeVideoClipId === clip.clipId;
  const hasDualStream = !!(clip.matteRgbUrl && clip.matteAlphaUrl);
  const hasWebmAlpha = !!clip.webmAlphaUrl && supportsVp9Alpha();
  // Phase 7g — show a loader while veed/fal matting is still in flight.
  const mattingPending = clip.transparency === 'matting' && clip.matteStatus === 'pending' && !hasDualStream && !hasWebmAlpha;
  const mattingFailed = clip.matteStatus === 'failed';
  const knownBg = isVeo(clip.provider) ? '#000000' : (clip.chromaKeyHex ?? '#000000');

  return (
    <div className="flex flex-col gap-4 pb-32">
      {/* Video player — first visible element. Floating Back pill sits over
          the top-left of the video; no header chrome above. */}
      <div className="relative overflow-hidden bg-black">
        <Link
          to={`/avatars/${id}`}
          className="absolute left-3 top-3 z-10 flex items-center gap-1 rounded-full bg-bg/70 px-2.5 py-1.5 text-xs font-semibold text-fg backdrop-blur active:opacity-80"
        >
          <ChevronLeft aria-hidden className="h-3.5 w-3.5" />
          {t('common.back')}
        </Link>
        {clip.status === 'ready' && clip.videoUrl ? (
          hasDualStream ? (
            <DualStreamVideo
              rgbUrl={clip.matteRgbUrl!}
              alphaUrl={clip.matteAlphaUrl!}
              poster={clip.posterUrl}
              knownBgHex={knownBg}
            />
          ) : hasWebmAlpha ? (
            <video src={clip.webmAlphaUrl} poster={clip.posterUrl} autoPlay loop muted playsInline className="h-full w-full object-contain" />
          ) : mattingPending ? (
            <div className="relative flex aspect-square w-full items-center justify-center text-fg-muted">
              {avatar.imageUrl && (
                <img src={avatar.imageUrl} alt={avatar.title} className="absolute inset-0 h-full w-full object-cover opacity-30" />
              )}
              <div className="relative flex flex-col items-center gap-2">
                <Loader2 aria-hidden className="h-10 w-10 animate-spin text-brand" />
                <p className="text-sm font-semibold">{t('clips.applyingMatte')}</p>
                <p className="text-[11px] text-fg-muted/80">{t('clipPage.matteEta')}</p>
              </div>
            </div>
          ) : (clip.transparency && clip.transparency !== 'none') ? (
            <ChromaKeyVideo
              src={clip.videoUrl}
              poster={clip.posterUrl ?? avatar.imageUrl}
              keyColor={clip.chromaKeyHex ?? '#FF00FF'}
            />
          ) : (
            <video src={clip.videoUrl} poster={clip.posterUrl} autoPlay loop muted playsInline className="h-full w-full object-contain" />
          )
        ) : clip.status === 'failed' ? (
          <div className="flex aspect-square items-center justify-center text-red-400">
            <Sparkles aria-hidden className="h-12 w-12 opacity-50" />
          </div>
        ) : (
          <div className="flex aspect-square items-center justify-center text-fg-muted">
            <Film aria-hidden className="h-12 w-12 opacity-30" />
          </div>
        )}
      </div>

      <div className="flex flex-col gap-4 px-4">
        {/* Title row — sits UNDER the video. */}
        <div className="flex items-center gap-2">
          <h1 className="line-clamp-1 flex-1 text-lg font-bold text-fg">{clip.label ?? t('clips.defaultLabel')}</h1>
          {isActive && (
            <span className="rounded-full bg-brand/15 px-2 py-0.5 text-[10px] font-bold uppercase text-brand">{t('clips.badge.active')}</span>
          )}
        </div>

        {mattingFailed && (
          <div className="flex items-start gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 p-2.5 text-xs text-fg">
            <AlertTriangle aria-hidden className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
            <div>
              <p className="font-semibold">{t('clipPage.matteFailedTitle')}</p>
              <p className="mt-0.5 text-[11px] text-fg-muted">
                {t(clip.matteFailureReason ? 'clipPage.matteFailedReason' : 'clipPage.matteFailed', { reason: clip.matteFailureReason })}
              </p>
            </div>
          </div>
        )}

        {/* Primary actions */}
        <div className="flex flex-wrap gap-2">
          {clip.status === 'ready' && !clip.archived && !isActive && (
            <PrimaryBtn label={t('clipPage.setActive')} onClick={onSetActive} variant="brand" />
          )}
          {puoCreare && clip.status === 'failed' && (
            <PrimaryBtn label={t('common.retry')} icon={<RefreshCw aria-hidden className="h-4 w-4" />} onClick={onRetry} variant="brand" />
          )}
          {puoCreare && clip.status === 'ready' && !clip.archived && (
            <PrimaryBtn label={t('clipPage.reroll')} onClick={onReroll} variant="muted" />
          )}
          <PrimaryBtn
            label={clip.archived ? t('clips.unarchive') : t('clips.archive')}
            icon={clip.archived ? <ArchiveRestore aria-hidden className="h-4 w-4" /> : <Archive aria-hidden className="h-4 w-4" />}
            onClick={onToggleArchive}
            variant="muted"
          />
          <PrimaryBtn
            label={t('common.delete')}
            icon={<Trash2 aria-hidden className="h-4 w-4" />}
            onClick={onDelete}
            variant="danger"
          />
        </div>

        {/* Disclosure toggle — text sections start collapsed. */}
        <button
          type="button"
          onClick={() => setDetailsOpen(o => !o)}
          className="self-center rounded-full bg-bg-secondary px-3 py-1.5 text-xs font-semibold text-fg-muted active:opacity-80"
        >
          {detailsOpen ? t('clipPage.hideDetails') : t('clipPage.showDetails')}
        </button>

        {detailsOpen && (
          <>
            {/* Motion prompt — editable. Submitting a non-empty edit creates
                a new clip with the modified action; same provider, duration,
                resolution, and transparency are carried over from this clip. */}
            {puoCreare && (
              <Section title={t('clipDetails.userPrompt')} subtitle={t('clipPage.editSubtitle')}>
                <textarea
                  value={editPrompt}
                  onChange={(e) => setEditPrompt(e.target.value)}
                  rows={4}
                  className="w-full resize-y rounded-xl border border-border bg-bg-secondary p-3 text-sm leading-relaxed text-fg outline-none focus:border-brand"
                  placeholder={t('clipPage.editPlaceholder')}
                />
                <PromptInIngleseHint />
                <button
                  type="button"
                  onClick={onRegenerate}
                  disabled={!editDirty}
                  className={cn(
                    'mt-1 flex items-center justify-center gap-1.5 self-start rounded-xl px-3 py-2 text-sm font-semibold transition-opacity active:opacity-80',
                    editDirty ? 'bg-brand text-brand-fg' : 'bg-bg-secondary text-fg-muted opacity-50',
                  )}
                >
                  <Wand2 aria-hidden className="h-4 w-4" />
                  {t('clipPage.regenerate')}
                </button>
              </Section>
            )}

            {sys && (
              <Section title={t('clipPage.systemPrompt')} subtitle={t('clipPage.systemPromptHint')}>
                <p className="whitespace-pre-wrap rounded-xl border border-amber-500/40 bg-amber-500/5 p-3 text-xs leading-relaxed text-fg-muted">
                  {sys}
                </p>
              </Section>
            )}

            {/* Model + options + seed */}
            <Section title={t('clipDetails.modelOptions')}>
              <div className="flex flex-col gap-1 rounded-xl bg-bg-secondary p-3 text-sm">
                <Row label={t('clipDetails.engine')} value={providerLabel} />
                <Row label={t('clipDetails.duration')} value={clip.durationSec ? `${clip.durationSec}s` : '—'} />
                <Row label={t('clipDetails.resolution')} value={clip.resolution ?? '—'} />
                <Row label={t('clipDetails.background')} value={transparencyLabel} />
                {clip.chromaKeyHex && <Row label={t('clipDetails.keyColor')} value={clip.chromaKeyHex.toUpperCase()} mono />}
                <Row label={t('clipPage.seed')} value={typeof clip.seed === 'number' ? String(clip.seed) : t('clipPage.seedRandom')} mono />
                <Row label={t('clipDetails.status')} value={clip.status} mono />
              </div>
            </Section>

            {/* Run metrics */}
            <Section title={t('clipDetails.run')}>
              <div className="flex flex-col gap-1 rounded-xl bg-bg-secondary p-3 text-sm">
                <Row label={t('clipDetails.cost')} value={costUsd ? `$${costUsd}` : '—'} icon={<DollarSign aria-hidden className="h-3.5 w-3.5" />} />
                <Row label={t('clipDetails.started')} value={fmtTime(clip.startedAt, lingua)} icon={<Clock aria-hidden className="h-3.5 w-3.5" />} />
                <Row label={t('clipDetails.completed')} value={fmtTime(clip.completedAt, lingua)} icon={<Clock aria-hidden className="h-3.5 w-3.5" />} />
                <Row label={t('clipDetails.wallTime')} value={fmtWall(clip.startedAt, clip.completedAt)} />
              </div>
            </Section>

            {/* Output URLs */}
            {(clip.videoUrl || clip.matteRgbUrl || clip.webmAlphaUrl) && (
              <Section title={t('clipDetails.output')}>
                <div className="flex flex-col gap-1 rounded-xl bg-bg-secondary p-3 text-xs">
                  {clip.videoUrl    && <FileRow label={t('clipDetails.fileMp4')}   url={clip.videoUrl} />}
                  {clip.matteRgbUrl && <FileRow label={t('clipDetails.fileRgb')}  url={clip.matteRgbUrl} />}
                  {clip.matteAlphaUrl && <FileRow label={t('clipDetails.fileAlpha')}   url={clip.matteAlphaUrl} />}
                  {clip.webmAlphaUrl && <FileRow label={t('clipDetails.fileWebm')} url={clip.webmAlphaUrl} />}
                </div>
              </Section>
            )}

            {/* Failure */}
            {clip.failureReason && (
              <Section title={t('clipDetails.failure')}>
                <p className="whitespace-pre-wrap rounded-xl border border-red-500/40 bg-red-500/10 p-3 text-xs text-fg">
                  {clip.failureReason}
                </p>
              </Section>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Section({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-1.5">
      <div className="flex flex-col">
        <h2 className="text-xs font-semibold uppercase text-fg-muted">{title}</h2>
        {subtitle && <p className="text-[11px] text-fg-muted/70">{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

function Row({ label, value, icon, mono }: { label: string; value: string; icon?: React.ReactNode; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase text-fg-muted">{icon}{label}</span>
      <span className={cn('text-right text-fg break-all', mono && 'font-mono text-xs')}>{value}</span>
    </div>
  );
}

function FileRow({ label, url }: { label: string; url: string }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="flex items-center justify-between gap-2 rounded-lg bg-bg-tertiary px-2 py-1.5 transition-opacity active:opacity-80"
    >
      <span className="font-semibold text-fg">{label}</span>
      <span className="line-clamp-1 max-w-[60%] font-mono text-[10px] text-fg-muted">{url.split('/').slice(-2).join('/')}</span>
    </a>
  );
}

function PrimaryBtn({
  label, icon, onClick, variant,
}: {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  variant: 'brand' | 'muted' | 'danger';
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-semibold transition-opacity active:opacity-80',
        variant === 'brand'  && 'bg-brand text-brand-fg',
        variant === 'muted'  && 'bg-bg-secondary text-fg',
        variant === 'danger' && 'bg-red-500/10 text-red-400',
      )}
    >
      {icon}
      {label}
    </button>
  );
}

export default AvatarClipDetail;
